<template>
  <!--
    The view for the Adventures Statistics
  -->
  <div class="installationDetail_Adventures">
    <Portlet title="Adventures">
      <LoadingPlaceholder v-if="loading" />
      <template v-else>
        <LoadingPlaceholder v-if="reportData" />
        <template v-if="reportData">
          <div class="progress mt-3">
            <div
              class="progress-bar"
              role="progressbar"
              :style="{ width: reportData.percentage + '%' }"
              :aria-valuenow="reportData.percentage"
              aria-valuemin="0"
              aria-valuemax="100"
            >
              {{ reportData.percentage + "%" }}
            </div>
          </div>
        </template>
        <div class="adventures-container">
          <label
            for="adventures"
            class="form-label"
          >Adventures</label>
          <div style="display: flex">
            <Multiselect
              id="adventures"
              v-model="selectedAdventure"
              tag-placeholder="Not found"
              placeholder="Select Adventure"
              :options="adventures"
              label="title"
              select-label="title"
              :multiple="false"
              :taggable="true"
              :searchable="true"
              :close-on-select="true"
              class="mb-3"
            />
            <button
              slot="button"
              class="btn btn-primary ml-2 btn-sm mb-3"
              :disabled="!selectedAdventure"
              @click="exportData(1, 1)"
            >
              <span>CSV</span>
            </button>
            <button
              slot="button"
              class="btn btn-primary ml-2 btn-sm mb-3"
              :disabled="!selectedAdventure"
              @click="exportData(1, 2)"
            >
              <span>XLSX</span>
            </button>
          </div>
          <div
            v-if="selectedAdventure"
            class="mb-3"
          >
            <a
              class="field-toggle"
              data-toggle="collapse"
              href="#collapseAdventureFields"
            >
              <span class="field-toggle-caret">&#9662;</span>
              Fields
            </a>
            <a
              class="field-toggle ml-2"
              data-toggle="collapse"
              href="#collapseAdventureDates"
            >
              <span class="field-toggle-caret">&#9662;</span>
              {{ dateRangeLabel(adventureDateFilter) }}
            </a>
            <div
              id="collapseAdventureFields"
              class="collapse"
            >
              <div class="field-checkboxes pt-2">
                <div
                  v-for="f in adventureFieldOptions"
                  :key="f.key"
                  class="form-check"
                >
                  <input
                    :id="'adventure-field-' + f.key"
                    v-model="selectedAdventureFields"
                    class="form-check-input"
                    type="checkbox"
                    :value="f.key"
                  >
                  <label
                    class="form-check-label"
                    :for="'adventure-field-' + f.key"
                  >{{ f.label }}</label>
                </div>
              </div>
            </div>
            <div
              id="collapseAdventureDates"
              class="collapse"
            >
              <AdventureExportDateFilter
                v-model="adventureDateFilter"
                :options="adventureDateOptions"
                id-prefix="adventure"
              />
            </div>
          </div>
          <label
            for="levels"
            class="form-label"
          >Levels</label>
          <div style="display: flex">
            <Multiselect
              id="levels"
              v-model="selectedLevel"
              tag-placeholder="Not found"
              placeholder="Select Level"
              :options="adventureLevels"
              label="title"
              select-label="title"
              :multiple="false"
              :taggable="true"
              :searchable="true"
              :close-on-select="true"
              class="mb-3"
            />
            <button
              slot="button"
              class="btn btn-primary ml-2 btn-sm mb-3"
              :disabled="!selectedLevel"
              @click="exportData(2, 1)"
            >
              <span>CSV</span>
            </button>
            <button
              slot="button"
              class="btn btn-primary ml-2 btn-sm mb-3"
              :disabled="!selectedLevel"
              @click="exportData(2, 2)"
            >
              <span>XLSX</span>
            </button>
          </div>
          <div
            v-if="selectedLevel"
            class="mb-3"
          >
            <a
              class="field-toggle"
              data-toggle="collapse"
              href="#collapseLevelFields"
            >
              <span class="field-toggle-caret">&#9662;</span>
              Fields
            </a>
            <a
              class="field-toggle ml-2"
              data-toggle="collapse"
              href="#collapseLevelDates"
            >
              <span class="field-toggle-caret">&#9662;</span>
              {{ dateRangeLabel(levelDateFilter) }}
            </a>
            <div
              id="collapseLevelFields"
              class="collapse"
            >
              <div class="field-checkboxes pt-2">
                <div
                  v-for="f in levelFieldOptions"
                  :key="f.key"
                  class="form-check"
                >
                  <input
                    :id="'level-field-' + f.key"
                    v-model="selectedLevelFields"
                    class="form-check-input"
                    type="checkbox"
                    :value="f.key"
                  >
                  <label
                    class="form-check-label"
                    :for="'level-field-' + f.key"
                  >{{ f.label }}</label>
                </div>
              </div>
            </div>
            <div
              id="collapseLevelDates"
              class="collapse"
            >
              <AdventureExportDateFilter
                v-model="levelDateFilter"
                :options="levelDateOptions"
                id-prefix="level"
              />
            </div>
          </div>
          <label
            for="rewards"
            class="form-label"
          >Rewards</label>
          <div style="display: flex">
            <Multiselect
              id="rewards"
              v-model="selectedReward"
              tag-placeholder="Not found"
              placeholder="Select Reward"
              :options="levelRewards"
              label="title"
              select-label="title"
              :multiple="false"
              :taggable="true"
              :searchable="true"
              :close-on-select="true"
              class="mb-3"
            />
            <button
              slot="button"
              class="btn btn-primary ml-2 btn-sm mb-3"
              :disabled="!selectedReward"
              @click="exportData(3, 1)"
            >
              <span>CSV</span>
            </button>
            <button
              slot="button"
              class="btn btn-primary ml-2 btn-sm mb-3"
              :disabled="!selectedReward"
              @click="exportData(3, 2)"
            >
              <span>XLSX</span>
            </button>
          </div>
          <div
            v-if="selectedReward"
            class="mb-3"
          >
            <a
              class="field-toggle"
              data-toggle="collapse"
              href="#collapseRewardFields"
            >
              <span class="field-toggle-caret">&#9662;</span>
              Fields
            </a>
            <a
              class="field-toggle ml-2"
              data-toggle="collapse"
              href="#collapseRewardDates"
            >
              <span class="field-toggle-caret">&#9662;</span>
              {{ dateRangeLabel(rewardDateFilter) }}
            </a>
            <div
              id="collapseRewardFields"
              class="collapse"
            >
              <div class="field-checkboxes pt-2">
                <div
                  v-for="f in rewardFieldOptions"
                  :key="f.key"
                  class="form-check"
                >
                  <input
                    :id="'reward-field-' + f.key"
                    v-model="selectedRewardFields"
                    class="form-check-input"
                    type="checkbox"
                    :value="f.key"
                  >
                  <label
                    class="form-check-label"
                    :for="'reward-field-' + f.key"
                  >{{ f.label }}</label>
                </div>
              </div>
            </div>
            <div
              id="collapseRewardDates"
              class="collapse"
            >
              <AdventureExportDateFilter
                v-model="rewardDateFilter"
                :options="rewardDateOptions"
                id-prefix="reward"
              />
            </div>
          </div>
        </div>
        <div class="statistic-info">
          <div
            v-if="selectedAdventure"
            class="mt-4"
          >
            <p class="title">
              Adventure:
            </p>
            <p>
              Total:
              <i>{{
                selectedAdventure.completed + selectedAdventure.inProgress
              }}</i>
            </p>
            <p>
              Completed: <i>{{ selectedAdventure.completed }}</i>
            </p>
            <p>
              In progress: <i>{{ selectedAdventure.inProgress }}</i>
            </p>
          </div>
          <div
            v-if="selectedLevel"
            class="mt-4"
          >
            <p class="title">
              Level:
            </p>
            <p>
              Completed: <i>{{ selectedLevel.completed }}</i>
            </p>
          </div>
          <div
            v-if="selectedReward"
            class="mt-4"
          >
            <p class="title">
              Reward:
            </p>
            <p>
              Completed: <i>{{ selectedReward.completed }}</i>
            </p>
          </div>
        </div>
        <!-- The export leaves out deleted users, these numbers still count them -->
        <p
          v-if="selectedAdventure"
          class="text-muted small mt-3 mb-0"
        >
          Deleted user accounts are not included in the export. The statistical
          values remain unchanged and may therefore differ from the number of
          entries in the export.
        </p>
        <div
          v-if="charData"
          class="mt-4"
        >
          <template v-if="!selectedReward">
            <kendo-chart
              :series-defaults="{ type: 'column', gap: 0.5, spacing: 0.3 }"
              :category-axis="{
                categories: charData.actions.map((action) => action.title),
                labels: { rotation: 'auto' },
              }"
              :value-axis="{ labels: { format: '{0}' } }"
              :tooltip="{ visible: true, format: '{0}' }"
            >
              <kendo-chart-series-item
                :name="'Completed Actions'"
                :data="charData.actions.map((action) => action.completed)"
              />
            </kendo-chart>

            <kendo-chart
              class="mt-4"
              :series-defaults="{ type: 'column' }"
              :category-axis="{
                categories: charData.badges.map((badge) => badge.title),
                labels: { rotation: 'auto' },
              }"
              :value-axis="{ labels: { format: '{0}' } }"
              :tooltip="{ visible: true, format: '{0}' }"
            >
              <kendo-chart-series-item
                :name="'Completed Badges'"
                :data="charData.badges.map((badge) => badge.completed)"
                :color="'#d3902f'"
              />
            </kendo-chart>

            <kendo-chart
              class="mt-4"
              :series-defaults="{ type: 'column' }"
              :category-axis="{
                categories: charData.awards.map((award) => award.title),
                labels: { rotation: 'auto' },
              }"
              :value-axis="{ labels: { format: '{0}' } }"
              :tooltip="{ visible: true, format: '{0}' }"
            >
              <kendo-chart-series-item
                :name="'Completed Awards'"
                :data="charData.awards.map((award) => award.completed)"
                :color="'#36A2EB'"
              />
            </kendo-chart>

            <kendo-chart
              class="mt-4"
              :series-defaults="{ type: 'column' }"
              :category-axis="{
                categories: charData.awards.map((award) => award.title),
                labels: { rotation: 'auto' },
              }"
              :value-axis="{ labels: { format: '{0}' } }"
              :tooltip="{ visible: true, format: '{0}' }"
            >
              <kendo-chart-series-item
                :name="'Redeemed Awards'"
                :data="charData.awards.map((award) => award.redeemed)"
                :color="'#ffef81'"
              />
            </kendo-chart>
          </template>
          <template v-else>
            <kendo-chart
              class="mt-4"
              :series-defaults="{ type: 'column' }"
              :category-axis="{
                categories: [charData.title],
                labels: { rotation: 'auto' },
              }"
              :value-axis="{ labels: { format: '{0}' } }"
              :tooltip="{ visible: true, format: '{0}' }"
            >
              <kendo-chart-series-item
                :name="`Completed ${
                  rewardType === 'badge' ? 'Badges' : 'Awards'
                }`"
                :data="[charData.completed]"
                :color="'#36A2EB'"
              />
            </kendo-chart>
            <kendo-chart
              v-if="rewardType === 'award'"
              class="mt-4"
              :series-defaults="{ type: 'column' }"
              :category-axis="{
                categories: [charData.title],
                labels: { rotation: 'auto' },
              }"
              :value-axis="{ labels: { format: '{0}' } }"
              :tooltip="{ visible: true, format: '{0}' }"
            >
              <kendo-chart-series-item
                :name="'Redeemed Awards'"
                :data="[charData.redeemed]"
                :color="'#ffef81'"
              />
            </kendo-chart>
          </template>
        </div>
      </template>
    </Portlet>
  </div>
</template>

<script>
import { responsiveKendoGridMixin } from "@/mixins/responsiveKendoGridMixin.js";

const BASE_FIELDS = [
  { key: "Email", label: "Email" },
  { key: "FirstName", label: "First name" },
  { key: "LastName", label: "Last name" },
  { key: "Country", label: "Country" },
  { key: "Language", label: "Language" },
  { key: "StartedAdventure", label: "Started Adventure", isDate: true },
  { key: "CompletedAdventure", label: "Completed Adventure", isDate: true },
  { key: "Tickets", label: "Tickets" },
];

const ADVENTURE_FIELDS = [
  ...BASE_FIELDS,
  { key: "CompletedActivities", label: "Completed Activities" },
  { key: "CurrentAdventurePoints", label: "Current Adventure Points" },
];

const LEVEL_FIELDS = [
  ...BASE_FIELDS,
  { key: "CompletedActivities", label: "Completed Activities" },
  { key: "CompletedSteps", label: "Completed Steps" },
  { key: "CurrentLevelPoints", label: "Current Level Points" },
  { key: "CurrentAdventurePoints", label: "Current Adventure Points" },
  { key: "FinishedLevel", label: "Finished Level", isDate: true },
];

function getRewardFields(rewardType) {
  const fields = [
    ...BASE_FIELDS,
    {
      key: "AchievedReward",
      label: `Achieved ${rewardType === "award" ? "Award" : "Badge"}`,
      isDate: true,
    },
  ];
  if (rewardType === "award") {
    fields.push({
      key: "RedeemedAward",
      label: "Redeemed Award",
      isDate: true,
    });
  }
  return fields;
}

// The dates a date range applies to until others are ticked: who started the Adventure, who
// finished the level, who achieved the badge or award
const DEFAULT_DATE_FIELDS = {
  adventure: ["StartedAdventure"],
  level: ["FinishedLevel"],
  reward: ["AchievedReward"],
};

function emptyDateFilter(dateFields) {
  return { from: "", to: "", dateFields: [...dateFields] };
}

function formatDay(day) {
  return day ? day.split("-").reverse().join(".") : "…";
}

export default {
  name: "InstallationDetailAdventures",
  components: {
    Multiselect: () => import("vue-multiselect"),
    AdventureExportDateFilter: () =>
      import("@/components/Installation/AdventureExportDateFilter.vue"),
  },
  mixins: [responsiveKendoGridMixin],
  props: {
    installationId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      adventures: [],
      selectedAdventure: null,
      selectedLevel: null,
      selectedReward: null,
      reportInterval: null,
      reportData: null,
      progress: 0,
      reportDone: false,
      selectedAdventureFields: ADVENTURE_FIELDS.map((f) => f.key),
      selectedLevelFields: LEVEL_FIELDS.map((f) => f.key),
      selectedRewardFields: [],
      adventureDateFilter: emptyDateFilter(DEFAULT_DATE_FIELDS.adventure),
      levelDateFilter: emptyDateFilter(DEFAULT_DATE_FIELDS.level),
      rewardDateFilter: emptyDateFilter(DEFAULT_DATE_FIELDS.reward),
    };
  },
  metaInfo() {
    return {
      title: "Adventures",
    };
  },
  computed: {
    adventureLevels() {
      return this.selectedAdventure ? this.selectedAdventure.levels : [];
    },
    levelRewards() {
      return this.selectedLevel
        ? [
            ...this.selectedLevel.awards.map((x) => ({
              ...x,
              title: `AWARD - ${x.title}`,
            })),
            ...this.selectedLevel.badges.map((x) => ({
              ...x,
              title: `BADGE - ${x.title}`,
            })),
          ]
        : [];
    },
    charData() {
      if (this.selectedReward) {
        return this.selectedReward;
      }
      if (this.selectedLevel) {
        return this.selectedLevel;
      }
      if (this.selectedAdventure) {
        return {
          actions: this.selectedAdventure.levels.flatMap((x) => x.actions),
          badges: this.selectedAdventure.levels.flatMap((x) => x.badges),
          awards: this.selectedAdventure.levels.flatMap((x) => x.awards),
        };
      }
      return null;
    },
    rewardType() {
      if (this.selectedAdventure && this.selectedReward) {
        const badges = this.selectedAdventure.levels.flatMap((x) => x.badges);
        const awards = this.selectedAdventure.levels.flatMap((x) => x.awards);
        return awards.some((x) => x.id === this.selectedReward.id)
          ? "award"
          : badges.some((x) => x.id === this.selectedReward.id)
          ? "badge"
          : null;
      }
      return null;
    },
    // The reward dropdown prefixes its titles with AWARD/BADGE; exports use the reward's own title
    selectedRewardTitle() {
      if (!this.selectedLevel || !this.selectedReward) return "";
      const reward = [
        ...this.selectedLevel.awards,
        ...this.selectedLevel.badges,
      ].find((x) => x.id === this.selectedReward.id);
      return reward ? reward.title : "";
    },
    adventureFieldOptions() {
      return ADVENTURE_FIELDS;
    },
    levelFieldOptions() {
      return LEVEL_FIELDS;
    },
    rewardFieldOptions() {
      return getRewardFields(this.rewardType);
    },
    adventureDateOptions() {
      return ADVENTURE_FIELDS.filter((f) => f.isDate);
    },
    levelDateOptions() {
      return LEVEL_FIELDS.filter((f) => f.isDate);
    },
    rewardDateOptions() {
      return this.rewardFieldOptions.filter((f) => f.isDate);
    },
  },
  watch: {
    // A new selection gets the default dates again but keeps the range, so the same month can
    // be exported for several Adventures, levels or rewards in a row
    selectedAdventure() {
      this.selectedLevel = null;
      this.selectedReward = null;
      this.selectedAdventureFields = ADVENTURE_FIELDS.map((f) => f.key);
      this.adventureDateFilter.dateFields = [...DEFAULT_DATE_FIELDS.adventure];
    },
    selectedLevel() {
      this.selectedReward = null;
      this.selectedLevelFields = LEVEL_FIELDS.map((f) => f.key);
      this.levelDateFilter.dateFields = [...DEFAULT_DATE_FIELDS.level];
    },
    selectedReward() {
      this.selectedRewardFields = getRewardFields(this.rewardType).map(
        (f) => f.key
      );
      this.rewardDateFilter.dateFields = [...DEFAULT_DATE_FIELDS.reward];
    },
  },
  mounted() {
    this.getStatistic();
  },
  beforeDestroy() {
    this.cancelInterval();
  },
  methods: {
    // Shows a set range on the collapsed toggle, so it is not forgotten when exporting
    dateRangeLabel(filter) {
      if (!filter.from && !filter.to) return "Date range";
      return `Date range: ${formatDay(filter.from)} – ${formatDay(filter.to)}`;
    },
    createInterval(installationId, id) {
      this.reportData = { percentage: 0 };
      this.reportDone = false;

      this.reportInterval = window.setInterval(() => {
        if (this.reportDone) return;
        this.getReportProgress(installationId, id);
      }, 2000);
    },
    cancelInterval() {
      window.clearInterval(this.reportInterval);
      this.reportInterval = null;
      this.reportDone = true;
      this.reportData = null;
    },
    getReportProgress(installationId, id) {
      this.axios
        .get(
          `/Gamification/GetExportAdventureData?installationId=${installationId}&id=${id}`
        )
        .then((response) => {
          if (this.reportDone) return;
          if (response.status == 200 && response.data) {
            this.reportData = response.data;
            if (this.reportData.base64) {
              this.reportDone = true;
              window.clearInterval(this.reportInterval);

              const link = document.createElement("a");
              link.href =
                "data:application/octet-stream;charset=utf-8;base64," +
                this.reportData.base64;
              link.target = "_blank";
              link.download = this.reportData.filename;
              link.click();

              this.cancelInterval();
            } else if (this.reportData.error) {
              // E.g. Skiline could not be asked which users were deleted: say why nothing
              // was downloaded instead of just stopping
              this.$snotify.error(this.reportData.error);
              this.cancelInterval();
            }
          } else {
            this.reportData = null;
          }
        });
    },
    exportData(type, exportType) {
      this.loading = true;
      const id =
        type === 1
          ? this.selectedAdventure.id
          : type === 2
          ? this.selectedLevel.id
          : this.selectedReward.id;
      const stepId =
        type === 2
          ? this.selectedLevel
            ? this.selectedLevel.stepId
            : ""
          : type === 3
          ? this.selectedReward
            ? this.selectedReward.stepId
            : ""
          : "";
      const selectedFields =
        type === 1
          ? this.selectedAdventureFields
          : type === 2
          ? this.selectedLevelFields
          : this.selectedRewardFields;
      const fields = encodeURIComponent(selectedFields.join(","));
      // Goes into the export's file name, so customers can tell the files apart
      const name = encodeURIComponent(
        (type === 1
          ? this.selectedAdventure.title
          : type === 2
          ? this.selectedLevel.title
          : this.selectedRewardTitle) || ""
      );
      const dateFilter =
        type === 1
          ? this.adventureDateFilter
          : type === 2
          ? this.levelDateFilter
          : this.rewardDateFilter;
      const dateFields = encodeURIComponent(dateFilter.dateFields.join(","));
      this.axios
        .get(
          `/Gamification/ExportAdventureData?installationId=${this.installationId}&id=${id}&type=${type}&exportType=${exportType}&stepId=${stepId}&rewardType=${this.rewardType}&fields=${fields}&name=${name}&from=${dateFilter.from}&to=${dateFilter.to}&dateFields=${dateFields}`
        )
        .then(() => {
          this.createInterval(this.installationId, id);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getStatistic() {
      this.loading = true;
      this.axios
        .get(
          `/Gamification/GetAdventureStatistic?installationId=${this.installationId}`
        )
        .then((response) => {
          if (response == null) {
            return;
          }
          if (response.data == null) {
            return;
          }
          this.adventures = response.data;
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style scoped lang="scss">
.installationDetail_Adventures {
  .statistic-info {
    display: flex;
    gap: 50px;
    .title {
      font-weight: bold;
      font-size: 15px;
    }
  }
  .field-toggle {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 14px;
    background-color: #eef1f5;
    border: 1px solid #ced4da;
    border-radius: 4px;
    color: #495057;
    font-weight: 600;
    font-size: 0.9rem;
    cursor: pointer;

    &:hover,
    &:focus {
      background-color: #e2e6ea;
      color: #495057;
      text-decoration: none;
    }

    .field-toggle-caret {
      display: inline-block;
      transition: transform 0.15s ease;
    }

    &.collapsed .field-toggle-caret {
      transform: rotate(-90deg);
    }
  }
  .field-checkboxes {
    column-width: 180px;
    column-gap: 24px;

    .form-check {
      break-inside: avoid;
      margin-bottom: 4px;
    }
  }
}
</style>
